import React, { useEffect } from 'react';
import {Link as RRDLink, Route, Routes, Navigate, Outlet, HashRouter, NavLink as RRDNavLink, matchRoutes, useLocation, useNavigate,  useParams} from 'react-router-dom';

/**
 *
 *  Collection of methods and components for handling routes
 *
 */

// :: {"className": Style class of link, "activeRoutes": Array of routes to check to see if this link should be "active", "activeClassName": class of "active" link style}
// React component used for a styled "link" element:
export function NavLink(props) {
  const onClick = () => {
    window.scrollTo(0, 0);

    if(props.onClick instanceof Function) {
      props.onClick();
    }
  }

  // :: STRING, [STRING] -> STRING
  // Returns CSS of "active" link if current route belongs to any of the given "active routes":
  const isActive = (activeRoutes, activeClassName) => {
    // If both properties are undefined return an empty STRING
    if(activeRoutes !== undefined && activeClassName !== undefined){
      // Verify that the activeRoutes property is an [STRING], else throw new error
      if(!(activeRoutes instanceof Array && activeRoutes.every(item => typeof item == "string"))){
        throw new Error("activeRoutes must be an ARRAY of STRING");
      }

      // Verify that the activeClassName property is a STRING, else throw new error
      if(typeof activeClassName !== "string"){
        throw new Error("activeClassName must be a STRING");
      }

      const routes = activeRoutes.map(route => ({ "path" : route }), []);
      return matchRoutes(routes, useLocation().pathname)
        ? " " + activeClassName
        : "";
    } 
    else {
      return "";
    }
  };

  const ref = React.createRef();
  
  useEffect(() => {
    const node = ref.current;
    // If the element has child nodes, set the data point attribute
    if(node.childNodes.length > 0) {
      node.firstChild.setAttribute("data-point", props.dataPoint ?? "");
    }
    // Remove the div wrapper
    node.replaceWith(...node.childNodes);
  });

  return (
    <div ref={ ref }>
      <RRDNavLink
        to={props.to}
        className={props.className + isActive(props.activeRoutes, props.activeClassName)}
        onClick = { onClick }>
        { props.children }
      </RRDNavLink>
    </div>
  );
}

// Link :: STRING, STRING, STRING, FUNCTION -> React.Component
// React component to allow a scroll to top of window on a link click from react-router-dom
export function Link(props) {
  const onClick = () => {
    // Scroll to top of the window
    window.scrollTo(0, 0);
    // if props.onClick is a function, run the function
    if(props.onClick instanceof Function) {
      props.onClick();
    }
  };

  const ref = React.createRef();
  
  useEffect(() => {
    const node = ref.current;
    // If the are child nodes set the data point attribute
    if(node.childNodes.length > 0) {
      node.firstChild.setAttribute("data-point", props.dataPoint ?? "");
    }
    // Remove the div wrapper
    node.replaceWith(...node.childNodes);
  });

  return (
    <div ref={ ref }>
      <RRDLink
        to={ props.to }
        className={ props.className ?? "" }
        onClick={ onClick }>
        { props.children }
      </RRDLink>
    </div>
  );
}

// :: VOID -> STRING -> VOID
// Exposes "useNavigate" hook for routing progamatically:
// NOTE: Needs to be initialzed in a component first which is why a function is returned for setting route:
// NOTE: also, while "onBefore" seems to work pretty well, "onAfter" sorta works...
export const useRoute = () => {
  let navigate = useNavigate(); // Hook for programatic routing
  let {search} = useLocation(); // Get search parameters to add to route

  return (route, state, onBefore, onAfter) => {
    if (typeof(onBefore) === "function") {
      onBefore();
    }

    const routeTo = !isNaN(parseInt(route)) ? route : route + search;

    window.scrollTo(0, 0);
    navigate(routeTo, state ?? {});

    if (typeof(onAfter) === "function") {
      onAfter();
    }
  }
}

const BCMSRouteDataContext = React.createContext(null);

// Provides context to child components in order to access data that was passed
// through a route
export function BCMSRouteData(props) {
    // Value is the "route" property set to the state within useLocation
    return (
        <BCMSRouteDataContext.Provider value={{ "route" : useLocation().state }}>
            { props.children }
        </BCMSRouteDataContext.Provider>
    );
}

// Hook to access the bcmsRouteData context
export const useBCMSRouteData = () => React.useContext(BCMSRouteDataContext);

/**
 *
 *  Other Exports
 *
 */

export {
  Route,
  Routes,
  Outlet,
  Navigate,
  useLocation,
  HashRouter,
  useParams,
  matchRoutes
};
